/**
 * Power Automate Flow Management API client
 */

import { requestWithRetry } from "../utils/http.js";
import type { AuthProvider } from "../auth/types.js";
import type {
  Flow,
  FlowRun,
  FlowRunAction,
  FlowRunActionRepetition,
  FlowPermission,
  FlowOwnerModification,
  Connection,
  Environment,
  ApiListResponse,
  ListFlowsOptions,
  CreateFlowRequest,
  UpdateFlowRequest,
  TriggerFlowRequest,
} from "./types.js";
import { createChildLogger } from "../utils/logger.js";
import {
  FlowNotFoundError,
  ApiError,
  RateLimitError,
  AuthorizationError,
  wrapApiCall, ValidationError } from "../utils/errors.js";
import {
  FLOW_API_BASE,
  FLOW_API_VERSION,
  DEFAULT_PAGE_SIZE,
  MAX_PAGE_SIZE,
} from "../utils/constants.js";
import { sanitizeErrorMessage, validateResourceLinkUrl, validateEnvironmentId, validateRowId, validateFlowRunId, validateFlowVersionId } from "../utils/security.js";
import { getOidFromToken } from "../utils/jwt-claims.js";

const apiLogger = createChildLogger({ module: "flow-api" });

export interface FlowManagementApiConfig {
  authProvider: AuthProvider;
  defaultEnvironmentId: string;
}

/**
 * Client for Power Automate Flow Management REST API
 */
export class FlowManagementApi {
  private readonly authProvider: AuthProvider;
  private readonly defaultEnvironmentId: string;

  constructor(config: FlowManagementApiConfig) {
    this.authProvider = config.authProvider;
    this.defaultEnvironmentId = config.defaultEnvironmentId;
  }

  /**
   * The environment used when a call passes none. Exposed so error messages
   * can name which environment was actually checked (connection lookups that
   * fail silently against the wrong environment are a documented time sink).
   */
  getDefaultEnvironmentId(): string {
    return this.defaultEnvironmentId;
  }

  /**
   * Return the current user's Azure AD object ID derived from the access token.
   * Used for client-side ownership filtering. Returns undefined if extraction fails.
   */
  async getCurrentUserOid(): Promise<string | undefined> {
    try {
      const token = await this.authProvider.getToken();
      return getOidFromToken(token);
    } catch {
      return undefined;
    }
  }

  /**
   * Get authorization header with bearer token
   */
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const token = await this.authProvider.getToken();
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }

  /**
   * Build API URL for flow operations
   */
  private buildUrl(
    environmentId: string,
    path: string,
    queryParams?: Record<string, string | number | undefined>
  ): string {
    validateEnvironmentId(environmentId);
    const base = `${FLOW_API_BASE}/providers/Microsoft.ProcessSimple/environments/${environmentId}${path}`;
    const params = new URLSearchParams();
    params.set("api-version", FLOW_API_VERSION);

    if (queryParams) {
      for (const [key, value] of Object.entries(queryParams)) {
        if (value !== undefined) {
          params.set(key, String(value));
        }
      }
    }

    return `${base}?${params.toString()}`;
  }

  /**
   * Handle API response errors
   */
  private async handleResponse<T>(
    response: Awaited<ReturnType<typeof requestWithRetry>>,
    context: string
  ): Promise<T> {
    const { statusCode, body } = response;

    if (statusCode === 429) {
      await body.text();
      const retryAfter = response.headers["retry-after"];
      const retryMs = retryAfter ? parseInt(String(retryAfter), 10) * 1000 : undefined;
      throw new RateLimitError(retryMs);
    }

    // Stream-read body with size limit to prevent OOM on large responses
    const MAX_RESPONSE_SIZE = 50 * 1024 * 1024; // 50MB
    const chunks: Buffer[] = [];
    let totalSize = 0;
    for await (const chunk of body as AsyncIterable<Uint8Array>) {
      totalSize += chunk.length;
      if (totalSize > MAX_RESPONSE_SIZE) {
        throw new ApiError(`${context}: response too large (max 50MB)`, 413);
      }
      chunks.push(Buffer.from(chunk));
    }
    const responseText = Buffer.concat(chunks).toString("utf-8");

    if (statusCode === 401) {
      throw new AuthorizationError(`${context}: Authentication failed`);
    }

    if (statusCode === 403) {
      const sanitizedBody = sanitizeErrorMessage(responseText);
      apiLogger.error({ statusCode, errorBody: sanitizedBody, context }, "Access denied - 403");
      throw new AuthorizationError(`${context}: Access denied - ${sanitizedBody}`);
    }

    if (statusCode === 404) {
      const sanitizedBody = sanitizeErrorMessage(responseText);
      apiLogger.error({ statusCode, errorBody: sanitizedBody, context }, "Not found - 404");
      throw new FlowNotFoundError(`${context}: ${sanitizedBody}`);
    }

    if (statusCode >= 400) {
      let errorDetails: unknown;
      try {
        errorDetails = JSON.parse(responseText);
      } catch {
        errorDetails = responseText;
      }
      throw new ApiError(`${context}: API error`, statusCode, errorDetails);
    }

    if (!responseText) {
      // Return a safe empty value that won't crash callers expecting .value
      return { value: [] } as unknown as T;
    }

    return JSON.parse(responseText) as T;
  }

  /**
   * List flows in an environment with optional pagination support
   */
  async listFlows(options: ListFlowsOptions = {}): Promise<{ flows: Flow[]; nextLink?: string }> {
    const envId = options.environment ?? this.defaultEnvironmentId;
    const top = Math.min(options.top ?? DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE);

    apiLogger.debug({ envId, options }, "Listing flows");

    const queryParams: Record<string, string | number | undefined> = {
      $top: top,
    };

    if (options.filter) {
      queryParams["$filter"] = options.filter;
    }

    if (options.skipToken) {
      queryParams["$skiptoken"] = options.skipToken;
    }

    const url = this.buildUrl(envId, "/flows", queryParams);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const data = await this.handleResponse<ApiListResponse<Flow>>(
        response,
        "List flows"
      );
      apiLogger.info({ count: data.value.length, hasMore: !!data.nextLink }, "Listed flows");
      return { flows: data.value, nextLink: data.nextLink };
    }, "List flows");
  }

  /**
   * Get a single flow by ID
   */
  async getFlow(flowId: string, environmentId?: string): Promise<Flow> {
    const { flow } = await this.getFlowWithEtag(flowId, environmentId);
    return flow;
  }

  /**
   * Get a flow plus its ETag when the service provides one (#8).
   *
   * The Flow API's ETag support is undocumented — the ETag response header (or an
   * `@odata.etag` body property) is captured opportunistically. Callers can pass it
   * back via `updateFlow`'s `ifMatch` for optimistic concurrency where the service
   * enforces it; when the service returns no ETag this degrades to a plain read.
   */
  async getFlowWithEtag(
    flowId: string,
    environmentId?: string
  ): Promise<{ flow: Flow; etag?: string }> {
    const safeFlowId = validateRowId(flowId);
    const envId = environmentId ?? this.defaultEnvironmentId;

    const url = this.buildUrl(envId, `/flows/${safeFlowId}`);
    apiLogger.debug({ flowId, envId, url }, "Getting flow");
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const headerEtag = response.headers["etag"];
      const flow = await this.handleResponse<Flow & { "@odata.etag"?: string }>(response, flowId);
      const etag = (Array.isArray(headerEtag) ? headerEtag[0] : headerEtag) ?? flow["@odata.etag"];
      apiLogger.info(
        { flowId, displayName: flow.properties.displayName, hasEtag: !!etag },
        "Retrieved flow"
      );
      return { flow, etag };
    }, `Get flow ${flowId}`);
  }

  /**
   * Get flow run history
   */
  async getFlowRuns(
    flowId: string,
    environmentId?: string,
    top?: number,
    status?: string
  ): Promise<FlowRun[]> {
    const envId = environmentId ?? this.defaultEnvironmentId;
    const limit = Math.min(top ?? DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE);

    const safeFlowId = validateRowId(flowId);
    apiLogger.debug({ flowId: safeFlowId, envId, limit, status }, "Getting flow runs");

    // The runs API filters server-side on status; without it a busy flow's
    // failures fall off the end of a page full of successes.
    const query: Record<string, string | number | undefined> = { $top: limit };
    if (status) {
      if (!/^[A-Za-z]+$/.test(status)) throw new ValidationError("Invalid run status filter");
      query["$filter"] = `status eq '${status}'`;
    }
    const url = this.buildUrl(envId, `/flows/${safeFlowId}/runs`, query);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const data = await this.handleResponse<ApiListResponse<FlowRun>>(
        response,
        `Get runs for ${flowId}`
      );
      apiLogger.info({ flowId, count: data.value.length }, "Retrieved flow runs");
      return data.value;
    }, `Get runs for ${flowId}`);
  }

  /**
   * Enable (start) a flow
   */
  async startFlow(flowId: string, environmentId?: string): Promise<void> {
    const safeFlowId = validateRowId(flowId);
    const envId = environmentId ?? this.defaultEnvironmentId;

    apiLogger.info({ flowId: safeFlowId, envId }, "Starting flow");

    const url = this.buildUrl(envId, `/flows/${safeFlowId}/start`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "POST", headers });
      await this.handleResponse<unknown>(response, `Start flow ${flowId}`);
      apiLogger.info({ flowId }, "Flow started");
    }, `Start flow ${flowId}`);
  }

  /**
   * Disable (stop) a flow
   */
  async stopFlow(flowId: string, environmentId?: string): Promise<void> {
    const safeFlowId = validateRowId(flowId);
    const envId = environmentId ?? this.defaultEnvironmentId;

    apiLogger.info({ flowId: safeFlowId, envId }, "Stopping flow");

    const url = this.buildUrl(envId, `/flows/${safeFlowId}/stop`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "POST", headers });
      await this.handleResponse<unknown>(response, `Stop flow ${flowId}`);
      apiLogger.info({ flowId }, "Flow stopped");
    }, `Stop flow ${flowId}`);
  }

  /**
   * Trigger a manual flow
   */
  async triggerFlow(
    flowId: string,
    triggerInputs?: TriggerFlowRequest,
    environmentId?: string
  ): Promise<FlowRun> {
    const envId = environmentId ?? this.defaultEnvironmentId;

    const safeFlowId = validateRowId(flowId);
    apiLogger.info({ flowId: safeFlowId, envId }, "Triggering flow");

    // Get the flow to find the actual trigger name
    const flow = await this.getFlow(safeFlowId, envId);
    const triggers = flow.properties.definition?.triggers ?? {};
    const triggerName = Object.keys(triggers)[0] ?? "manual";
    const trigger = triggers[triggerName];

    // The /run endpoint delivers the POST body to the trigger as its request
    // payload. Request triggers (manual/Button/PowerApp/Http) validate that
    // payload against inputs.schema, so the caller's fields must be posted
    // flat — the { body: ... } wrapper fails with TriggerInputSchemaMismatch
    // whenever the schema has required properties. When the definition is
    // unavailable the trigger name falls back to "manual", which is a Request
    // trigger by convention, so flat is the right bet there too. Non-Request
    // triggers keep the legacy wrapped shape (their /run ignores the payload).
    const sendFlat = !trigger || trigger.type === "Request";
    const payload = sendFlat ? (triggerInputs?.body ?? {}) : (triggerInputs ?? {});

    apiLogger.debug(
      { flowId: safeFlowId, triggerName, triggerType: trigger?.type, sendFlat },
      "Using trigger name"
    );

    const url = this.buildUrl(envId, `/flows/${safeFlowId}/triggers/${triggerName}/run`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      const run = await this.handleResponse<FlowRun>(
        response,
        `Trigger flow ${flowId}`
      );
      apiLogger.info({ flowId, runId: run.name }, "Flow triggered");
      return run;
    }, `Trigger flow ${flowId}`);
  }

  /**
   * Create a new flow
   */
  async createFlow(
    flowRequest: CreateFlowRequest,
    environmentId?: string
  ): Promise<Flow> {
    const envId = environmentId ?? this.defaultEnvironmentId;

    apiLogger.info(
      { envId, displayName: flowRequest.properties.displayName },
      "Creating flow"
    );

    const url = this.buildUrl(envId, "/flows");
    const headers = await this.getAuthHeaders();
    const requestBody = JSON.stringify(flowRequest);

    apiLogger.debug({ url, requestBody }, "Create flow request");

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, {
        method: "POST",
        headers,
        body: requestBody,
      });
      const flow = await this.handleResponse<Flow>(response, "Create flow");
      apiLogger.info(
        { flowId: flow.name, displayName: flow.properties.displayName },
        "Flow created"
      );
      return flow;
    }, "Create flow");
  }

  /**
   * Update an existing flow
   */
  async updateFlow(
    flowId: string,
    updateRequest: UpdateFlowRequest,
    environmentId?: string,
    options?: { ifMatch?: string }
  ): Promise<Flow> {
    const envId = environmentId ?? this.defaultEnvironmentId;

    const safeFlowId = validateRowId(flowId);
    apiLogger.info({ flowId: safeFlowId, envId, conditional: !!options?.ifMatch }, "Updating flow");

    const url = this.buildUrl(envId, `/flows/${safeFlowId}`);
    const headers = await this.getAuthHeaders();
    if (options?.ifMatch) {
      // Optimistic concurrency (#8): only applies where the service enforces If-Match.
      headers["If-Match"] = options.ifMatch;
    }

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, {
        method: "PATCH",
        headers,
        body: JSON.stringify(updateRequest),
      });
      if (response.statusCode === 412) {
        await response.body.text().catch(() => undefined);
        throw new ApiError(
          `Update flow ${flowId}: the flow changed since it was read (ETag mismatch). Re-read the flow and retry the update.`,
          412
        );
      }
      const flow = await this.handleResponse<Flow>(
        response,
        `Update flow ${flowId}`
      );
      apiLogger.info({ flowId }, "Flow updated");
      return flow;
    }, `Update flow ${flowId}`);
  }

  /**
   * Delete a flow
   */
  async deleteFlow(flowId: string, environmentId?: string): Promise<void> {
    const safeFlowId = validateRowId(flowId);
    const envId = environmentId ?? this.defaultEnvironmentId;

    apiLogger.info({ flowId: safeFlowId, envId }, "Deleting flow");

    const url = this.buildUrl(envId, `/flows/${safeFlowId}`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "DELETE", headers });
      await this.handleResponse<unknown>(response, `Delete flow ${flowId}`);
      apiLogger.info({ flowId }, "Flow deleted");
    }, `Delete flow ${flowId}`);
  }

  // ==========================================================================
  // Connection Management
  // ==========================================================================

  /**
   * List connections in an environment
   */
  async listConnections(environmentId?: string): Promise<Connection[]> {
    const envId = environmentId ?? this.defaultEnvironmentId;

    const url = this.buildUrl(envId, "/connections");
    apiLogger.debug({ envId, url }, "Listing connections");

    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const data = await this.handleResponse<ApiListResponse<Connection>>(
        response,
        "List connections"
      );
      apiLogger.info({ count: data.value.length }, "Listed connections");
      return data.value;
    }, "List connections");
  }

  /**
   * Get a single connection by ID
   */
  async getConnection(connectionId: string, environmentId?: string): Promise<Connection> {
    const safeConnectionId = validateRowId(connectionId);
    const envId = environmentId ?? this.defaultEnvironmentId;

    apiLogger.debug({ connectionId: safeConnectionId, envId }, "Getting connection");

    const url = this.buildUrl(envId, `/connections/${safeConnectionId}`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const connection = await this.handleResponse<Connection>(response, connectionId);
      apiLogger.info(
        { connectionId, displayName: connection.properties.displayName },
        "Retrieved connection"
      );
      return connection;
    }, `Get connection ${connectionId}`);
  }

  // ==========================================================================
  // Flow Run Actions (for detailed error diagnosis)
  // ==========================================================================

  /**
   * Get actions for a specific flow run (includes inputs/outputs/errors)
   */
  async getFlowRunActions(
    flowId: string,
    runId: string,
    environmentId?: string
  ): Promise<FlowRunAction[]> {
    const envId = environmentId ?? this.defaultEnvironmentId;

    const safeFlowId = validateRowId(flowId);
    const safeRunId = validateFlowRunId(runId);
    apiLogger.debug({ flowId: safeFlowId, runId: safeRunId, envId }, "Getting flow run actions");

    const url = this.buildUrl(envId, `/flows/${safeFlowId}/runs/${safeRunId}/actions`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      // The service pages this list (100 per page by default). A run of a
      // large flow easily has more, and stopping at the first page reported
      // "Actions: 100 total" for runs with several hundred — follow nextLink.
      const all: FlowRunAction[] = [];
      let next: string | undefined = url;
      const origin = new URL(url).origin;
      for (let page = 0; next && page < 50; page += 1) {
        if (new URL(next).origin !== origin) {
          throw new ValidationError("Run actions continuation link pointed at an unexpected host");
        }
        const response = await requestWithRetry(next, { method: "GET", headers });
        const data: ApiListResponse<FlowRunAction> = await this.handleResponse<ApiListResponse<FlowRunAction>>(
          response,
          `Get actions for run ${runId}`
        );
        all.push(...(data.value ?? []));
        next = data.nextLink;
      }
      apiLogger.info({ flowId, runId, count: all.length }, "Retrieved flow run actions");
      return all;
    }, `Get actions for run ${runId}`);
  }

  /**
   * Fetch content from a resource link (for action inputs/outputs)
   * Note: outputsLink URIs are Azure blob storage URLs with SAS tokens - don't add auth headers
   */
  async fetchResourceLink(uri: string): Promise<unknown> {
    const safeUri = validateResourceLinkUrl(uri);
    // Check if URI has SAS token (blob storage) - don't add auth header
    const hasSasToken = safeUri.includes("sig=") || safeUri.includes("sv=");
    const headers: Record<string, string> = hasSasToken
      ? { "Content-Type": "application/json" }
      : await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(safeUri, { method: "GET", headers });
      const data = await this.handleResponse<unknown>(response, "Fetch resource link");
      return data;
    }, "Fetch resource link");
  }

  async getFlowRunActionRepetitions(
    flowId: string,
    runId: string,
    actionName: string,
    environmentId?: string
  ): Promise<FlowRunActionRepetition[]> {
    const envId = environmentId ?? this.defaultEnvironmentId;

    const safeFlowId = validateRowId(flowId);
    const safeRunId = validateFlowRunId(runId);
    const safeActionName = encodeURIComponent(actionName);
    apiLogger.debug({ flowId: safeFlowId, runId: safeRunId, actionName }, "Getting action repetitions");

    const url = this.buildUrl(
      envId,
      `/flows/${safeFlowId}/runs/${safeRunId}/actions/${safeActionName}/repetitions`
    );
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const data = await this.handleResponse<ApiListResponse<FlowRunActionRepetition>>(
        response,
        `Get repetitions for action ${actionName}`
      );
      apiLogger.info(
        { flowId, runId, actionName, count: data.value.length },
        "Retrieved action repetitions"
      );
      return data.value;
    }, `Get repetitions for action ${actionName}`);
  }

  // ==========================================================================
  // Flow Sharing & Permissions
  // ==========================================================================

  /**
   * Get permissions for a flow
   */
  async getFlowPermissions(flowId: string, environmentId?: string): Promise<FlowPermission[]> {
    const envId = environmentId ?? this.defaultEnvironmentId;

    const safeFlowId = validateRowId(flowId);
    apiLogger.debug({ flowId: safeFlowId, envId }, "Getting flow permissions");

    const url = this.buildUrl(envId, `/flows/${safeFlowId}/permissions`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const data = await this.handleResponse<ApiListResponse<FlowPermission>>(
        response,
        `Get permissions for ${flowId}`
      );
      apiLogger.info({ flowId, count: data.value.length }, "Retrieved flow permissions");
      return data.value;
    }, `Get permissions for ${flowId}`);
  }

  /**
   * Modify flow owners (share/unshare)
   */
  async modifyFlowOwners(
    flowId: string,
    modifications: FlowOwnerModification[],
    environmentId?: string
  ): Promise<void> {
    const envId = environmentId ?? this.defaultEnvironmentId;

    const safeFlowId = validateRowId(flowId);
    apiLogger.info({ flowId: safeFlowId, envId, modifications }, "Modifying flow owners");

    const url = this.buildUrl(envId, `/flows/${safeFlowId}/modifyowners`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, {
        method: "POST",
        headers,
        body: JSON.stringify({ put: modifications }),
      });
      await this.handleResponse<unknown>(response, `Modify owners for ${flowId}`);
      apiLogger.info({ flowId }, "Flow owners modified");
    }, `Modify owners for ${flowId}`);
  }

  // ==========================================================================
  // Flow Run Control
  // ==========================================================================

  /**
   * Resubmit a failed or cancelled flow run
   */
  async resubmitRun(
    flowId: string,
    runId: string,
    environmentId?: string
  ): Promise<void> {
    const envId = environmentId ?? this.defaultEnvironmentId;

    const safeFlowId = validateRowId(flowId);
    const safeRunId = validateFlowRunId(runId);
    apiLogger.info({ flowId: safeFlowId, runId: safeRunId, envId }, "Resubmitting flow run");

    // Get the flow to find the actual trigger name (not always "manual")
    const flow = await this.getFlow(safeFlowId, envId);
    const triggers = flow.properties.definition?.triggers ?? {};
    const triggerName = Object.keys(triggers)[0] ?? "manual";

    apiLogger.debug({ flowId: safeFlowId, triggerName }, "Using trigger name for resubmit");

    const url = this.buildUrl(
      envId,
      `/flows/${safeFlowId}/triggers/${triggerName}/histories/${safeRunId}/resubmit`
    );
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "POST", headers });
      await this.handleResponse<unknown>(response, `Resubmit run ${runId}`);
      apiLogger.info({ flowId, runId }, "Flow run resubmitted");
    }, `Resubmit run ${runId}`);
  }

  /**
   * Cancel a running flow execution
   */
  async cancelRun(
    flowId: string,
    runId: string,
    environmentId?: string
  ): Promise<void> {
    const envId = environmentId ?? this.defaultEnvironmentId;

    const safeFlowId = validateRowId(flowId);
    const safeRunId = validateFlowRunId(runId);
    apiLogger.info({ flowId: safeFlowId, runId: safeRunId, envId }, "Cancelling flow run");

    const url = this.buildUrl(envId, `/flows/${safeFlowId}/runs/${safeRunId}/cancel`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "POST", headers });
      await this.handleResponse<unknown>(response, `Cancel run ${runId}`);
      apiLogger.info({ flowId, runId }, "Flow run cancelled");
    }, `Cancel run ${runId}`);
  }

  /** Fetch one saved version of a flow, including its full definition. */
  async getFlowVersion(
    flowId: string,
    versionId: string,
    environmentId?: string,
  ): Promise<Flow> {
    const envId = environmentId ?? this.defaultEnvironmentId;
    const safeFlowId = validateRowId(flowId);
    const safeVersion = validateFlowVersionId(versionId);
    apiLogger.debug({ flowId: safeFlowId, versionId: safeVersion, envId }, "Getting flow version");

    const url = this.buildUrl(envId, `/flows/${safeFlowId}/versions/${safeVersion}`);
    const headers = await this.getAuthHeaders();
    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      return this.handleResponse<Flow>(response, `Get version ${safeVersion}`);
    }, `Get version ${safeVersion}`);
  }

  /**
   * Fetch the trigger payload of a past run — the real inputs the flow was
   * invoked with, useful for replaying a scenario through test_flow.
   */
  async getRunTriggerInputs(
    flowId: string,
    runId: string,
    environmentId?: string,
  ): Promise<{ triggerName: string; outputs?: unknown; status?: string; startTime?: string }> {
    const envId = environmentId ?? this.defaultEnvironmentId;
    const safeFlowId = validateRowId(flowId);
    const safeRunId = validateFlowRunId(runId);
    apiLogger.debug({ flowId: safeFlowId, runId: safeRunId, envId }, "Getting run trigger payload");

    const url = this.buildUrl(envId, `/flows/${safeFlowId}/runs/${safeRunId}`, {
      $expand: "properties/trigger",
    });
    const headers = await this.getAuthHeaders();
    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const run = await this.handleResponse<FlowRun>(response, `Get run ${safeRunId}`);
      const trigger = run.properties.trigger as unknown as {
        name?: string;
        outputsLink?: { uri?: string };
        outputs?: unknown;
        status?: string;
        startTime?: string;
      };
      let outputs = trigger.outputs;
      // Large trigger payloads are served behind a link rather than inline.
      if (outputs === undefined && trigger.outputsLink?.uri) {
        outputs = await this.fetchResourceLink(trigger.outputsLink.uri);
      }
      return {
        triggerName: trigger.name ?? "unknown",
        outputs,
        status: trigger.status,
        startTime: trigger.startTime,
      };
    }, `Get run ${safeRunId} trigger payload`);
  }

  // ==========================================================================
  // Environment Management
  // ==========================================================================

  /**
   * List all accessible environments
   */
  async listEnvironments(): Promise<Environment[]> {
    apiLogger.debug("Listing environments");

    // Environment list doesn't need an environment ID in the path
    const base = `${FLOW_API_BASE}/providers/Microsoft.ProcessSimple/environments`;
    const params = new URLSearchParams();
    params.set("api-version", FLOW_API_VERSION);
    const url = `${base}?${params.toString()}`;

    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const data = await this.handleResponse<ApiListResponse<Environment>>(
        response,
        "List environments"
      );
      apiLogger.info({ count: data.value.length }, "Listed environments");
      return data.value;
    }, "List environments");
  }

  // ==========================================================================
  // Flow Versions
  // ==========================================================================

  /**
   * List all versions of a flow
   */
  async listFlowVersions(
    flowId: string,
    environmentId?: string
  ): Promise<Array<{ id: string; version: string; createdTime: string }>> {
    const envId = environmentId ?? this.defaultEnvironmentId;

    const safeFlowId = validateRowId(flowId);
    apiLogger.debug({ flowId: safeFlowId, envId }, "Listing flow versions");

    const url = this.buildUrl(envId, `/flows/${safeFlowId}/versions`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const data = await this.handleResponse<ApiListResponse<{
        name: string;
        properties: { createdTime: string };
      }>>(response, `List versions for ${flowId}`);

      const versions = data.value.map((v) => ({
        id: v.name,
        version: v.name,
        createdTime: v.properties.createdTime,
      }));

      apiLogger.info({ flowId, count: versions.length }, "Listed flow versions");
      return versions;
    }, `List versions for ${flowId}`);
  }

  // ==========================================================================
  // Flow Export/Import
  // ==========================================================================

  /**
   * Export a flow as a package
   */
  async exportFlow(
    flowId: string,
    environmentId?: string
  ): Promise<unknown> {
    const envId = environmentId ?? this.defaultEnvironmentId;

    const safeFlowId = validateRowId(flowId);
    apiLogger.debug({ flowId: safeFlowId, envId }, "Exporting flow");

    const url = this.buildUrl(envId, `/flows/${safeFlowId}/exportPackage`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const data = await this.handleResponse<unknown>(response, `Export flow ${flowId}`);
      apiLogger.info({ flowId }, "Flow exported");
      return data;
    }, `Export flow ${flowId}`);
  }

  // ==========================================================================
  // Approvals
  // ==========================================================================

  /**
   * List pending approvals
   */
  async listApprovals(
    environmentId?: string,
    top?: number
  ): Promise<Array<{
    id: string;
    title: string;
    status: string;
    requestedBy: string;
    createdTime: string;
  }>> {
    const envId = environmentId ?? this.defaultEnvironmentId;
    const limit = Math.min(top ?? 25, MAX_PAGE_SIZE);

    apiLogger.debug({ envId, limit }, "Listing approvals");

    // The approvals API rejects unfiltered lists (ListApprovalsFilterMissing):
    // it requires a $filter on properties/owner/id — the caller's own oid.
    const oid = await this.getCurrentUserOid();
    if (!oid) {
      throw new ApiError(
        "List approvals: could not determine the signed-in user's object ID (required for the approvals owner filter)",
        400
      );
    }
    const url = this.buildUrl(envId, "/approvals", {
      $top: limit,
      $filter: `properties/owner/id eq '${oid}'`,
    });
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const data = await this.handleResponse<ApiListResponse<{
        name: string;
        properties: {
          title: string;
          status: string;
          owner: { displayName?: string; email?: string };
          createdTime: string;
        };
      }>>(response, "List approvals");

      const approvals = data.value.map((a) => ({
        id: a.name,
        title: a.properties.title,
        status: a.properties.status,
        requestedBy: a.properties.owner?.displayName ?? a.properties.owner?.email ?? "unknown",
        createdTime: a.properties.createdTime,
      }));

      apiLogger.info({ count: approvals.length }, "Listed approvals");
      return approvals;
    }, "List approvals");
  }

  /**
   * Respond to an approval
   */
  async respondApproval(
    approvalId: string,
    response: "Approve" | "Reject",
    comments?: string,
    environmentId?: string
  ): Promise<void> {
    const envId = environmentId ?? this.defaultEnvironmentId;

    const safeApprovalId = validateRowId(approvalId);
    apiLogger.info({ approvalId: safeApprovalId, response, envId }, "Responding to approval");

    const url = this.buildUrl(envId, `/approvals/${safeApprovalId}/respond`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const resp = await requestWithRetry(url, {
        method: "POST",
        headers,
        body: JSON.stringify({
          response,
          comments: comments ?? "",
        }),
      });
      await this.handleResponse<unknown>(resp, `Respond to approval ${approvalId}`);
      apiLogger.info({ approvalId, response }, "Approval responded");
    }, `Respond to approval ${approvalId}`);
  }
}
